import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Package,
  Boxes,
  Truck,
  MapPin,
  ArrowRight,
  Tag,
  Layers,
  Shirt,
  BookOpen,
  Sparkles,
  Users,
  Briefcase,
  ShoppingCart,
  ListChecks,
  Archive,
  Server,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Logistique de proximité ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Logistique de proximité
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Une logistique de proximité pour vos livraisons de cartons
              {" "}{city.aCity}
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Nous savons que les semaines précédant un déménagement
              sont chargées. C&apos;est pourquoi notre service de
              livraison à domicile vient vous simplifier la vie en
              vous ravitaillant sans que vous ayez à vous déplacer.
            </p>
          </div>

          <div className="lg:col-span-5 space-y-4">
            {[
              {
                title: "Une distribution locale efficace",
                body: "Nous desservons l'ensemble des habitations et des zones d'activités de Bazemont, mais également les villages environnants comme Herbeville, Aulnay-sur-Mauldre, Maule et Flins-sur-Seine.",
                Icon: MapPin,
              },
              {
                title: "Confort et flexibilité",
                body: "Vos produits vous parviennent à plat, propres et parfaitement protégés. Vous économisez ainsi vos forces et le volume du coffre de votre voiture pour vous concentrer sur l'essentiel : vos cartons.",
                Icon: Truck,
              },
            ].map(({ title, body, Icon }) => (
              <div
                key={title}
                className="flex items-start gap-4 bg-white border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
              >
                <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <div>
                  <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                    {title}
                  </h3>
                  <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                    {body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Particuliers vs Professionnels (split) ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            B2C &amp; B2B
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Des équipements d&apos;emballage adaptés aux besoins des
            particuliers et des professionnels
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Chaque déménagement a ses propres exigences, qu&apos;il
            s&apos;agisse de vider une maison de famille ou de
            transférer une infrastructure d&apos;entreprise.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Particuliers — bleu charte */}
          <div className="relative bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-7 overflow-hidden">
            <span className="absolute top-0 left-0 h-1.5 w-full bg-[#237cb5]" />
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-[#cfdbe6] text-[10.5px] uppercase tracking-[0.18em] font-extrabold text-[#237cb5] mb-5">
              <Users className="h-3.5 w-3.5" strokeWidth={2.8} />
              Pour les particuliers
            </div>
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-12 w-12 rounded-xl bg-[#237cb5] text-white">
                <Boxes className="h-6 w-6" strokeWidth={2.4} />
              </span>
              <h3 className="font-display font-extrabold text-[20px] text-[#0f1d2b] leading-tight">
                Packs &amp; formats résidentiels
              </h3>
            </div>
            <p className="mt-4 text-[14.5px] text-[#3a4856] leading-relaxed">
              Nous proposons une large sélection de packs par taille
              de logement, des cartons penderie pour vos vêtements
              sur cintres, ainsi que des formats renforcés ou dédiés
              aux livres.
            </p>
            <ul className="mt-5 space-y-2.5 text-[13.5px] text-[#3a4856]">
              <li className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Packs Studio / T2 / T3 / Maison
              </li>
              <li className="flex items-center gap-2">
                <Shirt className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Cartons penderie sur cintres
              </li>
              <li className="flex items-center gap-2">
                <BookOpen className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Cartons livres &amp; charges denses
              </li>
            </ul>
            <Link
              to="/kits-cartons"
              className="mt-6 inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[13.5px] transition-colors"
              data-testid="city-bazemont-cta-particuliers"
            >
              <Boxes className="h-4 w-4" strokeWidth={2.6} />
              Voir les kits particuliers
              <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
            </Link>
          </div>

          {/* Professionnels — orange accent */}
          <div className="relative bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-7 overflow-hidden">
            <span className="absolute top-0 left-0 h-1.5 w-full bg-[#FF5722]" />
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-[#cfdbe6] text-[10.5px] uppercase tracking-[0.18em] font-extrabold text-[#FF5722] mb-5">
              <Briefcase className="h-3.5 w-3.5" strokeWidth={2.8} />
              Pour les professionnels
            </div>
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-12 w-12 rounded-xl bg-[#FF5722] text-white">
                <Archive className="h-6 w-6" strokeWidth={2.4} />
              </span>
              <h3 className="font-display font-extrabold text-[20px] text-[#0f1d2b] leading-tight">
                Archivage, calage &amp; palettes
              </h3>
            </div>
            <p className="mt-4 text-[14.5px] text-[#3a4856] leading-relaxed">
              Nous fournissons des solutions de calage de haute
              protection, des cartons d&apos;archivage solides pour
              vos documents administratifs et du film étirable
              technique pour sécuriser vos palettes et vos parcs
              informatiques.
            </p>
            <ul className="mt-5 space-y-2.5 text-[13.5px] text-[#3a4856]">
              <li className="flex items-center gap-2">
                <Archive className="h-4 w-4 text-[#FF5722]" strokeWidth={2.6} />
                Cartons d&apos;archivage administratifs
              </li>
              <li className="flex items-center gap-2">
                <Layers className="h-4 w-4 text-[#FF5722]" strokeWidth={2.6} />
                Calage haute protection
              </li>
              <li className="flex items-center gap-2">
                <Server className="h-4 w-4 text-[#FF5722]" strokeWidth={2.6} />
                Film étirable palettes &amp; parcs IT
              </li>
            </ul>
            <Link
              to="/emballage-protection"
              className="mt-6 inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#FF5722] hover:bg-[#e84a17] text-white font-bold text-[13.5px] transition-colors"
              data-testid="city-bazemont-cta-pros"
            >
              <Briefcase className="h-4 w-4" strokeWidth={2.6} />
              Voir les solutions pros
              <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
            </Link>
          </div>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Expérience d'une commande en ligne ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Boutique en ligne
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            L&apos;expérience d&apos;une commande sur Internet simple
            et intuitive
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Ne perdez plus votre temps dans les files d&apos;attente
            des magasins de bricolage du 78. Notre plateforme en ligne
            a été pensée pour vous offrir un parcours d&apos;achat
            fluide.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-6 md:p-7">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-12 w-12 rounded-xl bg-[#237cb5] text-white">
                <ListChecks className="h-6 w-6" strokeWidth={2.4} />
              </span>
              <h3 className="font-display font-extrabold text-[18px] text-[#0f1d2b] leading-tight">
                Un catalogue clair
              </h3>
            </div>
            <p className="mt-4 text-[14.5px] text-[#3a4856] leading-relaxed">
              Sélectionnez précisément vos articles à l&apos;unité ou
              optez pour nos formules clés en main en fonction du
              volume estimé de votre mobilier.
            </p>
            <Link
              to="/cartons-demenagement"
              className="mt-5 inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[13px] transition-colors"
              data-testid="city-bazemont-cta-catalogue"
            >
              <Package className="h-4 w-4" strokeWidth={2.6} />
              Parcourir le catalogue
              <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
            </Link>
          </div>

          <div className="bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-6 md:p-7">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-12 w-12 rounded-xl bg-[#237cb5] text-white">
                <ShoppingCart className="h-6 w-6" strokeWidth={2.4} />
              </span>
              <h3 className="font-display font-extrabold text-[18px] text-[#0f1d2b] leading-tight">
                Achat en quelques clics
              </h3>
            </div>
            <p className="mt-4 text-[14.5px] text-[#3a4856] leading-relaxed">
              Validez votre panier en toute sécurité, choisissez
              votre créneau de livraison et préparez vos pièces
              sereinement pendant que notre équipe logistique prépare
              votre commande.
            </p>
            <Link
              to="/kits-cartons"
              className="mt-5 inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[13px] transition-colors"
              data-testid="city-bazemont-cta-commander"
            >
              <ShoppingCart className="h-4 w-4" strokeWidth={2.6} />
              Lancer ma commande
              <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
            </Link>
          </div>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-t border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-bazemont-cta-back-dept"
        >
          <ShieldCheck className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
